import React, { useEffect, useState } from "react";
import { Check, X, AlertTriangle } from "lucide-react";
import commonService, { normalizeListResponse } from "../../services/otherServices";
import { getErrorMessage } from "../../services/apiClient";
import { useTheme } from "../../contexts/ThemeContext";
import ProgressBar, { PendingReasonBanner } from "./ProgressBar";
import { formatIST } from "../../utils/dateHelpers";

// Confirmed real row shape from /logs/* — {success, count, logs} — each row:
// { id, category, event_type, status, message, details, actor_email,
//   campaign_master_id, ip_address, created_at }
const readLogField = (log: any, ...keys: string[]) => {
  for (const key of keys) {
    if (log[key] !== undefined && log[key] !== null && log[key] !== "") return log[key];
  }
  return null;
};

const minByTime = (logs: any[]) =>
  logs.length
    ? logs.reduce((a, b) => (new Date(readLogField(a, "created_at")) < new Date(readLogField(b, "created_at")) ? a : b))
    : null;

const maxByTime = (logs: any[]) =>
  logs.length
    ? logs.reduce((a, b) => (new Date(readLogField(a, "created_at")) > new Date(readLogField(b, "created_at")) ? a : b))
    : null;

type StepState = "done" | "inprogress" | "error" | "warning" | "pending";

type Milestone = {
  key: string;
  label: string;
  time: string | null;
  // Explicit, per-step state driven by real signals — processing_status's
  // own scraping/verification `.status` ("completed"/"inprogress"/"pending")
  // for those two stages. Deliberately NOT derived from "is this just the
  // next step in the list" — that positional guess is what caused "Campaign
  // Sent" to show as "in progress" before anyone had even triggered a send.
  state: StepState;
  error: string | null;
};

// The `details` column on run_pipeline's per-step log entries (confirmed:
// sales_force_service.py's `_log()` helper) distinguishes the two identical
// "salesforce_list_created" / "salesforce_list_creation_failed" event_types
// via details.object ("Contact" vs "Account") — parsed defensively since the
// wire format (plain object vs JSON string) isn't guaranteed.
const readLogDetails = (log: any): any => {
  const raw = log?.details;
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
};

// Confirmed from sales_force_service.py's run_pipeline (the actual Salesforce
// send automation, category always "salesforce") — NOT the same as the
// scraping/verification pipeline. This is what actually happens when a
// campaign step launches: login, import the lead, create Contact + Account
// list views, apply the filter, then send. A failed send is logged as
// status="warning" by backend design (non-fatal — it retries later) rather
// than "failed", so it gets its own visual state instead of red "error".
//
// Scoped to ONE sequence step (via each log row's own `sequence_id`, set by
// run_pipeline's `_log()` helper on every event) — a campaign can have
// several steps (Email 1, Follow-up 1, ...), each with its own independent
// Salesforce run, so blending all of them together previously made an
// already-"sent" step's logs bleed into a still-"pending" follow-up's
// display (and vice versa), showing a fake "Salesforce Login — in progress"
// for a follow-up that hadn't even been triggered yet.
const buildSalesforceStepsForSequence = (step: any, logs: any[]): Milestone[] => {
  const scoped = logs.filter((l) => String(readLogField(l, "sequence_id")) === String(step.id));

  const byEvent = (eventType: string) => scoped.filter((l) => readLogField(l, "event_type") === eventType);

  const findByObject = (successType: string, failType: string, objectName: string) => ({
    success: byEvent(successType).find((l) => readLogDetails(l)?.object === objectName) || null,
    failure: byEvent(failType).find((l) => readLogDetails(l)?.object === objectName) || null,
  });

  const loginSuccess = maxByTime(byEvent("salesforce_login_success"));
  const loginFail = maxByTime([...byEvent("salesforce_login_failed"), ...byEvent("salesforce_connection_failed")]);

  const importSuccess = maxByTime(byEvent("salesforce_import_success"));
  const importFail = maxByTime(byEvent("salesforce_import_failed"));

  const contact = findByObject("salesforce_list_created", "salesforce_list_creation_failed", "Contact");
  const account = findByObject("salesforce_list_created", "salesforce_list_creation_failed", "Account");

  const filterSuccess = maxByTime(byEvent("salesforce_filter_applied"));
  const filterFail = maxByTime(byEvent("salesforce_filter_failed"));

  const sendSuccess = maxByTime(byEvent("salesforce_email_sent"));
  const sendWarning = maxByTime(byEvent("salesforce_email_failed"));

  // Now actually persisted (see websocket_api.py's sender_email_update_*
  // events) instead of only streaming over the websocket while the modal
  // was open and leaving no record afterward. Its own "started" log (no
  // guessing) is a real, not a guessed, "in progress" signal — separate
  // from the run_pipeline stages below, which have no such signal of
  // their own and rely on the "first pending stage" fallback further down.
  const senderStarted = maxByTime(byEvent("sender_email_update_started"));
  const senderSuccess = maxByTime(byEvent("sender_email_update_completed"));
  const senderFail = maxByTime(byEvent("sender_email_update_failed"));

  const senderMilestone: Milestone = {
    key: "sf_sender_verify",
    label: "Sender Email Verification",
    time: senderSuccess
      ? readLogField(senderSuccess, "created_at")
      : senderFail
        ? readLogField(senderFail, "created_at")
        : senderStarted
          ? readLogField(senderStarted, "created_at")
          : null,
    state: senderSuccess ? "done" : senderFail ? "error" : senderStarted ? "inprogress" : "pending",
    error: senderFail ? readLogField(senderFail, "message") || "Sender email verification failed" : null,
  };

  const stepFrom = (key: string, label: string, successLog: any, failLog: any): Milestone => ({
    key,
    label,
    time: successLog ? readLogField(successLog, "created_at") : failLog ? readLogField(failLog, "created_at") : null,
    state: successLog ? "done" : failLog ? "error" : "pending",
    error: failLog ? readLogField(failLog, "message") || `${label} failed` : null,
  });

  const steps: Milestone[] = [
    senderMilestone,
    stepFrom("sf_login", "Salesforce Login", loginSuccess, loginFail),
    stepFrom("sf_import", "Lead Imported", importSuccess, importFail),
    stepFrom("sf_contact_list", "Contact List Created", contact.success, contact.failure),
    stepFrom("sf_account_list", "Account List Created", account.success, account.failure),
    stepFrom("sf_filter", "Filter Applied", filterSuccess, filterFail),
    {
      key: "sf_send",
      label: "Email Sent",
      time: sendSuccess
        ? readLogField(sendSuccess, "created_at")
        : sendWarning
          ? readLogField(sendWarning, "created_at")
          : null,
      state: sendSuccess ? "done" : sendWarning ? "warning" : "pending",
      error: sendWarning ? readLogField(sendWarning, "message") || "Email send reported a warning — will retry" : null,
    },
  ];

  if (step.status === "sent") {
    // This step is confirmed sent by the sequence row itself — the
    // authoritative signal — regardless of how complete its own log trail
    // is. Force every stage to "done" so a step that really did send never
    // shows a stuck/incomplete breakdown just because an intermediate log
    // event wasn't captured. sent_at backfills any stage missing its own
    // timestamp.
    return steps.map((s) => ({
      ...s,
      state: "done",
      time: s.time || step.sent_at || null,
      error: null,
    }));
  }

  if (step.status === "failed") {
    // Whichever stage actually has a failure log stays red; nothing here
    // needs a synthetic override.
    return steps;
  }

  if (step.status === "inprogress" || step.status === "scheduled") {
    // This step's own Salesforce run is genuinely live right now (or about
    // to be picked up within seconds) — the first stage with no scoped
    // log yet is the one actually executing.
    const idx = steps.findIndex((s) => s.state === "pending");
    if (idx !== -1) steps[idx] = { ...steps[idx], state: "inprogress" };
    return steps;
  }

  // status === "pending": nothing has been triggered for this step yet —
  // leave every stage grey ("pending"), not a guessed "inprogress".
  return steps;
};

// "Available after 3 days", "Available after <date>", or "" for step 1 /
// once due — mirrors CampaignReview.tsx's canLaunchNext()/getRemainingDays()
// so the progress modal explains WHEN a pending follow-up actually unlocks
// instead of just saying "pending".
const getStepAvailability = (step: any, allSteps: any[]): string => {
  if (step.status !== "pending") return "";

  if (step.step_order === 1) return "Waiting to be launched";

  const previous = allSteps.find((s) => s.step_order === step.step_order - 1);

  if (!previous?.sent_at) {
    return `Available ${step.delay_days || 0} day${step.delay_days === 1 ? "" : "s"} after "${previous?.step_name || "the previous step"}" sends`;
  }

  const dueAt = new Date(previous.sent_at);
  dueAt.setDate(dueAt.getDate() + (step.delay_days || 0));

  if (new Date() >= dueAt) return "Due now — waiting for the worker to pick it up";

  return `Available after ${formatIST(dueAt)} IST`;
};

// salesforce_account is populated on activity_log rows (log_event's own
// kwarg) but is NOT serialized on GET /campaign/list or /campaign/details —
// confirmed by reading both response builders. This is the only place to
// recover which account a campaign actually used.
const extractSalesforceAccount = (logs: any[]): number | null => {
  const withAccount = logs.filter((l) => readLogField(l, "salesforce_account") !== null);
  const latest = maxByTime(withAccount);
  return latest ? Number(readLogField(latest, "salesforce_account")) : null;
};

// Real event_types confirmed from activity_log_service.py call sites (see
// AdminLogsPage.tsx) — aggregated into campaign-level milestones since a
// campaign can have many per-lead scrape/verify events. Ordered to match the
// app's own "Upload → Strategy → Review & Launch" stepper (CampaignStepper).
const buildTimeline = (campaign: any, logs: any[]): Milestone[] => {
  const ps = campaign.processing_status || {};

  const byEvent = (eventType: string) => logs.filter((l) => readLogField(l, "event_type") === eventType);

  const scrapingStatus = ps.scraping?.status; // "completed" | "inprogress" | "pending"
  const verificationStatus = ps.verification?.status;
  const sequenceDone = !!ps.sequence?.exists;

  const scrapeStart = minByTime(byEvent("lead_scrape_started"));
  const scrapeEnd = maxByTime([...byEvent("lead_scrape_completed"), ...byEvent("lead_scrape_giveup")]);

  const verifyStart = minByTime(byEvent("email_verify_started"));
  const verifyEnd = maxByTime([...byEvent("email_verify_completed"), ...byEvent("email_verify_timeout")]);

  // Deliberately no "error"/red state on these two campaign-level stages —
  // a single lead permanently giving up (CAPTCHA unsolved after retries,
  // verification timeout, etc.) doesn't block the rest of the campaign or
  // its own readiness, so it's shown the same as any other still-working
  // stage rather than as an alarming failure. Per-lead failures are still
  // visible (and actionable via "Reset Failed Domains") on the Leads &
  // Email Summary page.
  const stageState = (
    isDone: boolean,
    isRunning: boolean
  ): StepState => {
    if (isDone) return "done";
    if (isRunning) return "inprogress";
    return "pending";
  };

  return [
    {
      key: "upload",
      label: "File Uploaded",
      time: campaign.created_at || null,
      state: "done",
      error: null,
    },
    {
      key: "strategy",
      label: "Strategy Created",
      // No dedicated log event for this — processing_status.sequence is the
      // authoritative (and only) signal, so there's no exact timestamp and
      // no "in progress" state (it's an atomic save, not a running process).
      time: null,
      state: sequenceDone ? "done" : "pending",
      error: null,
    },
    {
      key: "scrape_start",
      label: "Scraping Started",
      time: scrapeStart ? readLogField(scrapeStart, "created_at") : null,
      state: scrapeStart || scrapingStatus === "completed" ? "done" : scrapingStatus === "inprogress" ? "inprogress" : "pending",
      error: null,
    },
    {
      key: "scrape_end",
      label: "Scraping Completed",
      time: scrapeEnd ? readLogField(scrapeEnd, "created_at") : null,
      state: stageState(scrapingStatus === "completed", scrapingStatus === "inprogress"),
      error: null,
    },
    {
      key: "verify_start",
      label: "Verification Started",
      time: verifyStart ? readLogField(verifyStart, "created_at") : null,
      state: verifyStart || verificationStatus === "completed" ? "done" : verificationStatus === "inprogress" ? "inprogress" : "pending",
      error: null,
    },
    {
      key: "verify_end",
      label: "Verification Completed",
      time: verifyEnd ? readLogField(verifyEnd, "created_at") : null,
      state: stageState(verificationStatus === "completed", verificationStatus === "inprogress"),
      error: null,
    },
  ];
};

const formatGap = (fromIso: string, toIso: string) => {
  const ms = new Date(toIso).getTime() - new Date(fromIso).getTime();
  if (!isFinite(ms) || ms < 0) return null;

  const mins = Math.round(ms / 60000);

  if (mins < 1) return "< 1 min later";
  if (mins < 60) return `${mins} min later`;

  const hrs = Math.floor(mins / 60);
  const remMins = mins % 60;

  if (hrs < 24) return `${hrs}h ${remMins}m later`;

  const days = Math.floor(hrs / 24);
  const remHrs = hrs % 24;

  return `${days}d ${remHrs}h later`;
};

// Shared renderer for both the main lifecycle timeline and the Salesforce
// send breakdown — connected circles (Amazon-delivery style): green check
// (done), blue (actively running), amber (non-fatal warning, e.g. a send
// that will auto-retry), red with hover tooltip (failed), grey (not reached).
const TimelineList: React.FC<{ steps: Milestone[] }> = ({ steps }) => {
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === "dark";

  return (
  <div>
    {steps.map((step, i) => {
      const isDone = step.state === "done";
      const isError = step.state === "error";
      const isWarning = step.state === "warning";
      const isCurrent = step.state === "inprogress";
      const isLast = i === steps.length - 1;

      // Walk back to the nearest earlier step that actually has a
      // timestamp — some steps (e.g. "Strategy Created") have none, and
      // comparing against them would silently hide a real gap.
      let prevTime: string | null = null;
      for (let j = i - 1; j >= 0; j--) {
        if (steps[j].time) {
          prevTime = steps[j].time;
          break;
        }
      }
      const gap = step.time && prevTime ? formatGap(prevTime, step.time) : null;

      const circleColor = isError
        ? "#dc2626"
        : isWarning
          ? "#f59e0b"
          : isDone
            ? "#16a34a"
            : isCurrent
              ? "#2563eb"
              : "#d1d5db";

      const textColor = isError
        ? "#dc2626"
        : isWarning
          ? "#b45309"
          : isDone
            ? (isDark ? "#e2e8f0" : "#111827")
            : isCurrent
              ? "#2563eb"
              : "#9ca3af";

      return (
        <div key={step.key} style={{ display: "flex", gap: "12px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div
              title={step.error || undefined}
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "50%",
                background: circleColor,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                cursor: step.error ? "help" : "default",
              }}
            >
              {isError || isWarning ? (
                <AlertTriangle size={12} color="#fff" />
              ) : isDone ? (
                <Check size={13} color="#fff" />
              ) : null}
            </div>
            {!isLast && (
              <div
                style={{
                  width: "2px",
                  flex: 1,
                  minHeight: "28px",
                  background: isDone || isError || isWarning
                    ? (isDark ? "rgba(34,197,94,0.3)" : "#d1fae5")
                    : (isDark ? "rgba(148,163,184,0.2)" : "#e5e7eb"),
                }}
              />
            )}
          </div>

          <div style={{ paddingBottom: "22px" }}>
            <div style={{ fontSize: "13.5px", fontWeight: isCurrent ? 700 : 600, color: textColor }}>
              {step.label}
              {isError ? " — failed" : isWarning ? " — warning" : isCurrent ? " — in progress" : ""}
            </div>

            {step.time ? (
              <div style={{ fontSize: "12px", color: "#6b7280", marginTop: "2px" }}>
                {formatIST(step.time)} IST
                {gap && <span style={{ color: "#9ca3af" }}> · {gap}</span>}
              </div>
            ) : (
              <div style={{ fontSize: "12px", color: "#9ca3af", marginTop: "2px" }}>
                {isError ? "Failed" : isDone ? "Completed" : isCurrent ? "In progress" : "Not started yet"}
              </div>
            )}

            {step.error && (
              <div
                style={{
                  fontSize: "12px",
                  color: isWarning ? "#b45309" : "#dc2626",
                  marginTop: "4px",
                  maxWidth: "320px",
                }}
              >
                {step.error}
              </div>
            )}
          </div>
        </div>
      );
    })}
  </div>
  );
};

/**
 * Amazon-delivery-style campaign tracker: progress bars for scraping/
 * verification counts, plus a chronological milestone timeline built from
 * /logs/campaign/{id} — each step shows when it happened and how long after
 * the previous step, greyed out if not reached yet, red with a hover tooltip
 * if that stage errored.
 */
const CampaignProgressModal: React.FC<{ campaign: any; onClose: () => void }> = ({ campaign, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [logItems, setLogItems] = useState<any[] | null>(null);
  const [sequenceSteps, setSequenceSteps] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Seeded from the campaign prop (whatever the list page had at the
  // moment "Progress" was clicked), then kept current by the SSE feed
  // below for as long as the modal stays open.
  const [liveCampaignStatus, setLiveCampaignStatus] = useState<string | undefined>(campaign.status);
  const [liveProcessingStatus, setLiveProcessingStatus] = useState<any>(campaign.processing_status || null);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  const fetchSequenceSteps = () => {
    commonService
      .getSequence(campaign.id)
      .then((res) => {
        setSequenceSteps(res.data?.data || []);
      })
      .catch(() => {
        // Non-critical — the lifecycle timeline above still works; the
        // per-step Salesforce breakdown just won't render without this.
        setSequenceSteps([]);
      });
  };

  useEffect(() => {
    let cancelled = false;

    commonService
      .logsForCampaign(campaign.id)
      .then((res) => {
        if (cancelled) return;
        const { items } = normalizeListResponse(res);
        setLogItems(items);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(getErrorMessage(err, "Failed to load campaign timeline"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    fetchSequenceSteps();

    // Live updates — new activity-log rows get appended (not refetched
    // from scratch) so the timeline never regresses/flickers, and
    // campaign/processing status stay current without a manual refresh. A
    // new log almost always means a sequence step's own status changed
    // too (sent/failed/inprogress), so that's the cue to refresh it.
    const unsubscribe = commonService.subscribeCampaignProgress(campaign.id, {
      onUpdate: (payload) => {
        if (cancelled) return;

        setLiveCampaignStatus(payload.campaign_status);
        setLiveProcessingStatus(payload.processing_status);
        setLastUpdated(payload.server_time);

        if (payload.new_logs?.length > 0) {
          setLogItems((prev) => [...(prev || []), ...payload.new_logs]);
          fetchSequenceSteps();
        }
      },
      onError: (message) => {
        console.error("Campaign progress stream error:", message);
      },
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign.id]);

  // Pure derivations from the accumulated logs + live status — recomputed
  // on every update instead of held as separate state, so there's no way
  // for the timeline to drift out of sync with logItems/status.
  //
  // processing_status must be merged in too, not just status — buildTimeline
  // reads campaign.processing_status for its scraping/verification stages,
  // and without this override it kept reading the stale snapshot captured
  // when the modal first opened, while the progress bars below read the
  // live SSE value. That mismatch is what let the Timeline show a green
  // "Scraping Completed" check at the same moment the bar above it read
  // "1/20 (5%), still scraping".
  const mergedCampaign = {
    ...campaign,
    status: liveCampaignStatus ?? campaign.status,
    processing_status: liveProcessingStatus || campaign.processing_status,
  };
  const timeline = logItems ? buildTimeline(mergedCampaign, logItems) : null;
  const salesforceAccount = logItems ? extractSalesforceAccount(logItems) : null;

  const processingStatus = mergedCampaign.processing_status;
  const { effectiveTheme } = useTheme();
  const isDark = effectiveTheme === "dark";
  const cardBg = isDark ? "#0f1630" : "#fff";
  const borderColor = isDark ? "rgba(148,163,184,0.15)" : "#e5e7eb";
  const textPrimary = isDark ? "#e2e8f0" : undefined;
  const textSecondary = isDark ? "#94a3b8" : "#6b7280";

  return (
    <div style={modalOverlay}>
      <div style={{ width: "100%", maxWidth: "480px", background: cardBg, borderRadius: "18px", overflow: "hidden", maxHeight: "85vh", display: "flex", flexDirection: "column" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${borderColor}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: "17px", fontWeight: 700, color: textPrimary }}>{campaign.file_name}</h3>
            <div style={{ fontSize: "12px", color: textSecondary, marginTop: "4px" }}>
              Where this campaign has reached
              {lastUpdated && (
                <> · Live — updated {formatIST(lastUpdated, { hour: "2-digit", minute: "2-digit", second: "2-digit" })} IST</>
              )}
            </div>
          </div>
          <button onClick={onClose} style={{ ...closeBtn, color: textSecondary }}>
            <X size={18} />
          </button>
        </div>

        <div style={{ padding: "22px", overflowY: "auto" }}>
          {processingStatus && (
            <>
              <ProgressBar
                label="Email Scraping"
                completed={processingStatus.scraping?.completed_count ?? 0}
                total={processingStatus.scraping?.total_count ?? 0}
                status={processingStatus.scraping?.status}
              />
              <ProgressBar
                label="Email Verification"
                completed={processingStatus.verification?.completed_count ?? 0}
                total={processingStatus.verification?.total_count ?? 0}
                status={processingStatus.verification?.status}
              />

              <PendingReasonBanner reasons={processingStatus.pending_reason || []} />
            </>
          )}

          <div style={{ fontSize: "13px", fontWeight: 700, color: textPrimary || "#111827", marginBottom: "14px" }}>Timeline</div>

          {loading ? (
            <div style={{ color: "#9ca3af", fontSize: "13px" }}>Loading timeline...</div>
          ) : error ? (
            <div style={{ color: "#b91c1c", fontSize: "13px" }}>{error}</div>
          ) : (
            <>
              <TimelineList steps={timeline || []} />

              <div
                style={{
                  fontSize: "13px",
                  fontWeight: 700,
                  color: textPrimary || "#111827",
                  marginTop: "22px",
                  marginBottom: "6px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                Sending via Salesforce
                {salesforceAccount !== null && (
                  <span
                    style={{
                      background: isDark ? "rgba(139,92,246,0.2)" : "#ede9fe",
                      color: isDark ? "#c4b5fd" : "#6d28d9",
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "2px 9px",
                      borderRadius: "999px",
                    }}
                  >
                    Account #{salesforceAccount}
                  </span>
                )}
              </div>

              {(sequenceSteps || []).map((step) => {
                const availability = getStepAvailability(step, sequenceSteps || []);
                const stepSteps = logItems ? buildSalesforceStepsForSequence(step, logItems) : [];

                return (
                  <div key={step.id} style={{ marginBottom: "18px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: availability ? "4px" : "10px",
                      }}
                    >
                      <span style={{ fontSize: "12.5px", fontWeight: 700, color: textPrimary || "#111827" }}>
                        {step.step_name}
                      </span>
                      <span
                        style={{
                          fontSize: "10px",
                          fontWeight: 700,
                          textTransform: "uppercase",
                          letterSpacing: "0.3px",
                          padding: "2px 8px",
                          borderRadius: "999px",
                          background: step.status === "sent"
                            ? (isDark ? "rgba(34,197,94,0.2)" : "#dcfce7")
                            : step.status === "failed"
                              ? (isDark ? "rgba(239,68,68,0.2)" : "#fee2e2")
                              : step.status === "inprogress" || step.status === "scheduled"
                                ? (isDark ? "rgba(37,99,235,0.2)" : "#dbeafe")
                                : (isDark ? "rgba(148,163,184,0.15)" : "#f1f5f9"),
                          color: step.status === "sent"
                            ? "#16a34a"
                            : step.status === "failed"
                              ? "#dc2626"
                              : step.status === "inprogress" || step.status === "scheduled"
                                ? "#2563eb"
                                : (isDark ? "#94a3b8" : "#64748b"),
                        }}
                      >
                        {step.status}
                      </span>
                    </div>

                    {availability && (
                      <div style={{ fontSize: "11.5px", color: "#9ca3af", marginBottom: "10px" }}>
                        {availability}
                      </div>
                    )}

                    <TimelineList steps={stepSteps} />
                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const modalOverlay: React.CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(15,23,42,0.55)",
  zIndex: 9999,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  backdropFilter: "blur(4px)",
};

const closeBtn: React.CSSProperties = {
  border: "none",
  background: "transparent",
  cursor: "pointer",
  color: "#6b7280",
};

export default CampaignProgressModal;
